import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient, { clearApiCache } from '../services/apiClient';

/**
 * Strip empty optional FK fields and normalize dates for project create/update payloads.
 *
 * @param {object} projectData - Raw project form payload
 * @returns {object} API-safe project payload
 */
const sanitizeProjectPayload = (projectData) => {
    const formatted = {
        ...projectData,
        start_date : projectData.start_date || null,
        end_date   : projectData.end_date || null,
    };

    if (!formatted.project_manager_id) {
        delete formatted.project_manager_id;
    }

    return formatted;
};

/**
 * Flatten DRF validation errors into a single user-facing message.
 *
 * @param {object} err - Axios error from apiClient
 * @param {string} fallback - Default message when no detail is available
 * @returns {string} Human-readable error message
 */
const extractProjectApiError = (err, fallback) => {
    const data = err?.response?.data;
    if (!data) {
        return fallback;
    }
    if (typeof data === 'string') {
        return data;
    }
    if (data.detail) {
        return String(data.detail);
    }
    const parts = Object.entries(data).map(([field, value]) => {
        const message = Array.isArray(value) ? value.join(', ') : String(value);
        return `${field}: ${message}`;
    });
    return parts.join('; ') || fallback;
};

/**
 * Custom hook for managing project data and operations
 * Centralizes project CRUD operations and state management
 */
export const useProjects = (user) => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastFetch, setLastFetch] = useState(null);
    const userRef = useRef(user);
    const isFetchingRef = useRef(false);
    const isCypressTestRun = typeof window !== 'undefined' && Boolean(window.Cypress);

    // Update user ref when user changes
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    const fetchProjects = useCallback(async (forceRefresh = false) => {
        // Don't fetch if no user is available
        if (!userRef.current) {
            console.log('No user available, skipping projects fetch');
            setLoading(false);
            return;
        }

        // Define cache key with user ID for session-specific caching
        const cacheKey = `/projects/${userRef.current?.id || 'anonymous'}`;

        // Stale-while-revalidate: show expired cache immediately, refresh in background.
        let hasStaleCache = false;
        if (!forceRefresh && !isCypressTestRun) {
            const cachedData = sessionStorage.getItem(cacheKey);
            if (cachedData) {
                try {
                    const parsedCache = JSON.parse(cachedData);
                    const cacheAge = Date.now() - (parsedCache.timestamp || 0);
                    if (cacheAge < 1800000) { // 30 minutes — resource_planning is heavy; list rarely changes second-to-second
                        if (process.env.NODE_ENV === 'development') {
                            console.log('useProjects: Using cached data, age:', Math.round(cacheAge / 1000), 'seconds');
                        }
                        setProjects(parsedCache.projects || []);
                        setLastFetch(Date.now());
                        setLoading(false);
                        return;
                    }

                    hasStaleCache = true;
                    if (process.env.NODE_ENV === 'development') {
                        console.log(
                            'useProjects: Using stale cache while refreshing, age:',
                            Math.round(cacheAge / 1000),
                            'seconds'
                        );
                    }
                    setProjects(parsedCache.projects || []);
                    setLastFetch(parsedCache.timestamp || Date.now());
                    setLoading(false);
                } catch (cacheError) {
                    console.warn('useProjects: Invalid cache data, clearing cache');
                    sessionStorage.removeItem(cacheKey);
                }
            }
        }

        // Prevent concurrent fetches - check after cache check
        if (isFetchingRef.current) {
            if (process.env.NODE_ENV === 'development') {
                console.log('useProjects: Fetch already in progress, skipping');
            }
            return;
        }

        isFetchingRef.current = true;

        try {
            if (!hasStaleCache) {
                setLoading(true);
            }
            setError(null);

            if (process.env.NODE_ENV === 'development') {
                console.log('Current user:', userRef.current);
            }

            // Only clear cache if force refresh is requested
            if (forceRefresh) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('useProjects: Force refresh requested, clearing cache');
                }
                sessionStorage.removeItem(cacheKey);

                // Also clear the API client cache, since apiClient caches GETs
                // (including /projects/resource_planning/) independently of sessionStorage.
                clearApiCache('/projects/resource_planning/');
                clearApiCache('/projects/');
            }

            // Use the optimized resource_planning endpoint with retry logic
            const response = await apiClient.getWithRetry('/projects/resource_planning/');

            // Extract projects from ProjectViewSet response format
            let projectsData = [];
            const responseData = response?.data || {};
            if (responseData.projects) {
                projectsData = responseData.projects;
                console.log('Successfully loaded projects:', projectsData.length);
            } else {
                console.log('No projects found in response');
                projectsData = [];
            }

            if (projectsData.length === 0) {
                console.log('No projects found. User role:', userRef.current?.role);
            }

            console.log('useProjects: Setting projects state:', projectsData);
            setProjects(projectsData);
            setLastFetch(Date.now());

            if (!isCypressTestRun) {
                // Cache the successful response with timestamp
                const cacheData = {
                    projects: projectsData,
                    timestamp: Date.now(),
                    userId: userRef.current?.id
                };
                sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            }

            console.log('useProjects: Projects state set, current projects length:', projectsData.length, '- cached successfully');
        } catch (err) {
            console.error('Error fetching projects:', err);

            // Fallback to regular projects endpoint if resource_planning fails
            try {
                console.log('Falling back to regular projects endpoint...');
                const fallbackResponse = await apiClient.getWithRetry('/projects/');
                const fallbackDataRaw = fallbackResponse?.data || {};
                const fallbackData = Array.isArray(fallbackDataRaw) ? fallbackDataRaw :
                    fallbackDataRaw.results ? fallbackDataRaw.results : [];

                setProjects(fallbackData);
                setLastFetch(Date.now());
                setError(null);

                if (!isCypressTestRun) {
                    // Cache the fallback response with timestamp
                    const fallbackCacheData = {
                        projects: fallbackData,
                        timestamp: Date.now(),
                        userId: userRef.current?.id,
                        source: 'fallback'
                    };
                    sessionStorage.setItem(cacheKey, JSON.stringify(fallbackCacheData));
                }
            } catch (fallbackErr) {
                console.error('Fallback projects fetch also failed:', fallbackErr);

                // Set a more user-friendly error message
                let errorMessage = 'Failed to load projects';
                if (fallbackErr.code === 'ECONNABORTED') {
                    errorMessage = 'Request timed out. Please try again.';
                } else if (fallbackErr.response?.status === 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else if (fallbackErr.response?.data?.detail) {
                    errorMessage = fallbackErr.response.data.detail;
                }

                setError(errorMessage);
            }
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [isCypressTestRun]);

    const createProject = useCallback(async (projectData) => {
        try {
            const formattedData = sanitizeProjectPayload(projectData);
            const response = await apiClient.post('/projects/', formattedData);
            setProjects(prev => [...prev, response.data]);

            // Clear cache to ensure fresh data on next fetch
            sessionStorage.removeItem('/projects/-{}');

            return { success: true, data: response.data };
        } catch (err) {
            console.error('Error creating project:', err);
            return {
                success: false,
                error: extractProjectApiError(err, 'Failed to create project')
            };
        }
    }, []);

    const updateProject = useCallback(async (projectId, projectData) => {
        try {
            const formattedData = sanitizeProjectPayload(projectData);
            const response = await apiClient.put(`/projects/${projectId}/`, formattedData);
            setProjects(prev => prev.map(p => p.id === projectId ? response.data : p));

            // Clear cache to ensure fresh data on next fetch
            sessionStorage.removeItem('/projects/-{}');

            return { success: true, data: response.data };
        } catch (err) {
            console.error('Error updating project:', err);
            return {
                success: false,
                error: extractProjectApiError(err, 'Failed to update project')
            };
        }
    }, []);

    const deleteProject = useCallback(async (projectId) => {
        try {
            const response = await apiClient.delete(`/projects/${projectId}/`);

            if (response.status === 204) {
                setProjects(prev => prev.filter(p => p.id !== projectId));
                sessionStorage.removeItem('/projects/-{}');
                return { success: true, action: 'deleted' };
            }

            const { action, message, project: updatedProject } = response.data || {};
            if (updatedProject) {
                setProjects(prev => prev.map(p => (
                    p.id === projectId ? { ...p, ...updatedProject } : p
                )));
            }
            sessionStorage.removeItem('/projects/-{}');
            return { success: true, action: action || 'archived', message };
        } catch (err) {
            console.error('Error removing project:', err);
            return {
                success: false,
                error: err.response?.data?.detail || err.response?.data?.message || 'Failed to remove project'
            };
        }
    }, []);



    const userId = user?.id;

    // Auto-refresh mechanism - DISABLED aggressive polling to prevent frequent refreshing
    useEffect(() => {
        if (!userId) {
            if (process.env.NODE_ENV === 'development') {
                console.log('useProjects: No user available, skipping projects fetch');
            }
            setLoading(false);
            return;
        }

        // Initial fetch when user id becomes available (stable primitive — not whole user object).
        fetchProjects();

        // DISABLED: Aggressive auto-refresh that was causing frequent refreshing
        // Users can manually refresh when needed via refresh button
        // This prevents the constant refreshing issue reported by users

        /* COMMENTED OUT: Aggressive polling causing UX issues
        // Set up polling with much longer intervals to prevent flickering
        const startPolling = () => {
            if (intervalRef.current) clearInterval(intervalRef.current);

            intervalRef.current = setInterval(() => {
                if (isActiveRef.current && document.visibilityState === 'visible') {
                    // Only refresh if data is older than 5 minutes to prevent disruption
                    if (!lastFetch || Date.now() - lastFetch > 300000) {
                        fetchProjects(true);
                    }
                }
            }, 600000); // Increased to 10 minutes to reduce impact
        };

        // Handle window focus/visibility changes - reduced frequency
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                isActiveRef.current = true;
                // Only refresh when tab becomes visible if it's been more than 10 minutes
                if (!lastFetch || Date.now() - lastFetch > 600000) {
                    fetchProjects(true);
                }
                startPolling();
            } else {
                isActiveRef.current = false;
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
            }
        };

        // Removed focus/blur handlers that were too aggressive

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Start initial polling
        startPolling();

        // Cleanup
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        */

    }, [userId, fetchProjects]);



    // Removed excessive console logging to improve performance

    return {
        projects,
        loading,
        error,
        lastFetch,
        fetchProjects,
        createProject,
        updateProject,
        deleteProject
    };
}; 